import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { blogApi } from "../../api/blogApi";
import { useAuth } from "../../context/AuthContext";

const T = {
  bg: "var(--color-background)",
  bgSecond: "var(--color-background-secondary)",
  border: "var(--color-border-secondary)",
  text: "var(--color-text-primary)",
  textMuted: "var(--color-text-secondary)",
  accent: "#378ADD",
  danger: "#E24B4A",
  radius: "8px",
};

const mdComponents = {
  h1: ({ children }) => (
    <h1
      style={{
        fontSize: "2em",
        fontWeight: 700,
        margin: "1.2em 0 0.5em",
        color: T.text,
        borderBottom: `1px solid ${T.border}`,
        paddingBottom: "0.3em",
      }}
    >
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2
      style={{
        fontSize: "1.5em",
        fontWeight: 700,
        margin: "1.2em 0 0.5em",
        color: T.text,
        borderBottom: `1px solid ${T.border}`,
        paddingBottom: "0.25em",
      }}
    >
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3
      style={{
        fontSize: "1.2em",
        fontWeight: 600,
        margin: "1em 0 0.4em",
        color: T.text,
      }}
    >
      {children}
    </h3>
  ),
  p: ({ children }) => (
    <p style={{ margin: "0.8em 0", lineHeight: "1.85", color: T.text }}>
      {children}
    </p>
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      style={{ color: T.accent, textDecoration: "underline" }}
    >
      {children}
    </a>
  ),
  ul: ({ children }) => (
    <ul style={{ margin: "0.8em 0", paddingLeft: "1.5em", color: T.text }}>
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol style={{ margin: "0.8em 0", paddingLeft: "1.5em", color: T.text }}>
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li style={{ margin: "0.3em 0", lineHeight: "1.7" }}>{children}</li>
  ),
  blockquote: ({ children }) => (
    <blockquote
      style={{
        margin: "1em 0",
        padding: "10px 16px",
        borderLeft: `4px solid ${T.accent}`,
        background: `${T.accent}0d`,
        borderRadius: "0 6px 6px 0",
        color: T.textMuted,
      }}
    >
      {children}
    </blockquote>
  ),
  code: ({ inline, children }) =>
    inline ? (
      <code
        style={{
          background: T.bgSecond,
          border: `1px solid ${T.border}`,
          borderRadius: "4px",
          padding: "2px 6px",
          fontSize: "0.875em",
          fontFamily: "monospace",
          color: T.accent,
        }}
      >
        {children}
      </code>
    ) : (
      <pre
        style={{
          background: T.bgSecond,
          border: `1px solid ${T.border}`,
          borderRadius: T.radius,
          padding: "16px",
          overflowX: "auto",
          margin: "1em 0",
        }}
      >
        <code
          style={{
            fontSize: "0.875em",
            fontFamily: "monospace",
            color: T.text,
          }}
        >
          {children}
        </code>
      </pre>
    ),
  img: ({ src, alt }) => (
    <img
      src={src}
      alt={alt}
      style={{
        maxWidth: "100%",
        borderRadius: T.radius,
        margin: "1em 0",
        display: "block",
      }}
    />
  ),
  hr: () => (
    <hr
      style={{
        border: "none",
        borderTop: `1px solid ${T.border}`,
        margin: "1.5em 0",
      }}
    />
  ),
  table: ({ children }) => (
    <div style={{ overflowX: "auto", margin: "1em 0" }}>
      <table
        style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}
      >
        {children}
      </table>
    </div>
  ),
  th: ({ children }) => (
    <th
      style={{
        padding: "8px 12px",
        background: T.bgSecond,
        border: `1px solid ${T.border}`,
        fontWeight: 600,
        textAlign: "left",
      }}
    >
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td style={{ padding: "8px 12px", border: `1px solid ${T.border}` }}>
      {children}
    </td>
  ),
};

const BlogDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ✅ 조회수를 이미 올렸는지 추적 — StrictMode 이중 실행 / 재렌더링 방어
  const viewedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const fetchPost = async () => {
      try {
        setLoading(true);

        // ✅ 조회수 먼저 증가 (중복 방지)
        if (!viewedRef.current) {
          viewedRef.current = true;
          await blogApi.increaseView(id).catch(() => {});
        }

        // ✅ 그 다음 게시글 조회
        const { data } = await blogApi.getOne(id);

        if (!cancelled) setPost(data);
      } catch (e) {
        if (!cancelled) setError("게시글을 불러올 수 없습니다.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPost();

    return () => {
      cancelled = true;
    };
  }, [id]);

  // id가 바뀌면 다시 조회수 올릴 수 있도록 ref 초기화
  useEffect(() => {
    viewedRef.current = false;
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;
    setDeleting(true);
    try {
      await blogApi.remove(id);
      navigate("/blog", { replace: true });
    } catch {
      alert("삭제에 실패했습니다.");
      setDeleting(false);
    }
  };

  if (loading)
    return (
      <div
        style={{
          maxWidth: "760px",
          margin: "0 auto",
          padding: "80px 24px",
          textAlign: "center",
          color: T.textMuted,
        }}
      >
        불러오는 중…
      </div>
    );

  if (error || !post)
    return (
      <div
        style={{
          maxWidth: "760px",
          margin: "0 auto",
          padding: "80px 24px",
          textAlign: "center",
        }}
      >
        <p style={{ color: T.danger }}>{error || "게시글이 없습니다."}</p>
        <button
          onClick={() => navigate("/blog")}
          style={{
            marginTop: "16px",
            background: "none",
            border: "none",
            cursor: "pointer",
            color: T.accent,
          }}
        >
          ← 목록으로
        </button>
      </div>
    );

  return (
    <div style={{ maxWidth: "760px", margin: "0 auto", padding: "48px 24px" }}>
      <button
        onClick={() => navigate("/blog")}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          color: T.textMuted,
          fontSize: "13px",
          marginBottom: "28px",
          padding: 0,
        }}
      >
        ← 목록으로
      </button>

      {!isAdmin && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "20px" }}>
          <button
            onClick={() => navigate("/blog/login")}
            style={{
              padding: "9px 16px",
              border: `1px solid ${T.border}`,
              borderRadius: T.radius,
              background: "transparent",
              color: T.text,
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            관리자 로그인
          </button>
        </div>
      )}

      <h1
        style={{
          fontSize: "28px",
          fontWeight: 700,
          color: T.text,
          margin: "0 0 16px",
          lineHeight: 1.4,
        }}
      >
        {post.title}
      </h1>

      <div
        style={{
          display: "flex",
          gap: "16px",
          fontSize: "13px",
          color: T.textMuted,
          marginBottom: "16px",
        }}
      >
        <span>
          {new Date(post.created_at).toLocaleDateString("ko-KR", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </span>
        <span>조회 {post.views?.toLocaleString()}</span>
        {post.updated_at && post.updated_at !== post.created_at && (
          <span>수정됨</span>
        )}
      </div>

      {post.tags?.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "6px",
            marginBottom: "28px",
          }}
        >
          {post.tags.map((tag) => (
            <span
              key={typeof tag === "string" ? tag : tag.name}
              onClick={() =>
                navigate(
                  `/blog?tag=${typeof tag === "string" ? tag : tag.name}`
                )
              }
              style={{
                fontSize: "13px",
                color: T.accent,
                cursor: "pointer",
                background: `${T.accent}15`,
                border: `1px solid ${T.accent}30`,
                borderRadius: "99px",
                padding: "3px 12px",
              }}
            >
              #{typeof tag === "string" ? tag : tag.name}
            </span>
          ))}
        </div>
      )}

      <hr
        style={{
          border: "none",
          borderTop: `1px solid ${T.border}`,
          marginBottom: "36px",
        }}
      />

      <div style={{ minHeight: "200px" }}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
          {post.content}
        </ReactMarkdown>
      </div>

      {isAdmin && (
        <div
          style={{
            display: "flex",
            gap: "8px",
            justifyContent: "flex-end",
            marginTop: "48px",
            paddingTop: "24px",
            borderTop: `1px solid ${T.border}`,
          }}
        >
          <button
            onClick={() => navigate(`/blog/${id}/edit`)}
            style={{
              padding: "9px 20px",
              border: `1px solid ${T.border}`,
              borderRadius: T.radius,
              background: "transparent",
              color: T.text,
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            수정
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            style={{
              padding: "9px 20px",
              border: "none",
              borderRadius: T.radius,
              background: T.danger,
              color: "#fff",
              cursor: deleting ? "not-allowed" : "pointer",
              fontSize: "14px",
              opacity: deleting ? 0.6 : 1,
            }}
          >
            {deleting ? "삭제 중…" : "삭제"}
          </button>
        </div>
      )}
    </div>
  );
};

export default BlogDetailPage;
